import { useState, useEffect, useRef, type ReactNode } from 'react'
import { apiClient, useAuthStore } from '@indipia/shared'
import { Upload, Button, Alert, Badge, Skeleton } from '@indipia/ui-core'
import type { NavFn } from '../pages/SchedulerPage'
import { StepIndicator } from './SiteSelectView'
import { ErrorBanner } from '../components/AsyncState'
import { InfoBanner } from '../components/InfoBanner'
import { useGoogleDrivePicker } from '../hooks/useGoogleDrivePicker'
import type { RawExtract } from './ExtractRawView2'
import type { ExtractWork, ExtractResult } from './extractTypes'


type Props = {
  navigate: NavFn
  onResult: (r: ExtractResult) => void
  /** LLM raw 추출 결과를 데이터 확인 화면으로 넘긴다 — 업로드한 원본 파일도 함께 넘겨 공종표에서 '원문 보기'로 볼 수 있게 한다 */
  onRawResult: (r: RawExtract | null, file?: File | null) => void
  /** 새 파일을 던지는 순간 이전 추출·매칭·선택 상태를 전부 비운다 */
  onReset: () => void
  /** 분석 요청에 전달할 현장 id (초기화 흐름이면 초기화 대상 현장) */
  selectedSiteId?: number
  /** 초기화(재생성) 흐름 여부 — true 면 [이전]이 생성 방법 선택(create-upload)으로 이동 */
  isResetFlow?: boolean
}

const ACCEPT = '.xlsx,.xls,.pdf,.png,.jpg,.jpeg'
const MAX_FILE_MB = 50
const MAX_FILE_BYTES = MAX_FILE_MB * 1024 * 1024

function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const isValidFile = (f: File) => /\.(xlsx|xls|pdf|png|jpg|jpeg)$/i.test(f.name)

type ErrShape = { code?: string; name?: string; response?: { status?: number; data?: { code?: string } } }

/** 요청 실패를 원인별 안내 문구로 바꾼다 — 사용자가 직접 취소한 경우는 null */
function uploadErrorMessage(err: unknown): string | null {
  const e = (err ?? {}) as ErrShape
  if (e.code === 'ERR_CANCELED' || e.name === 'CanceledError' || e.name === 'AbortError') return null
  const status = e.response?.status
  const code = e.response?.data?.code
  if (status === 413 || code === 'FILE_TOO_LARGE') return `파일이 너무 커요(${MAX_FILE_MB}MB까지 올릴 수 있어요)`
  if (code === 'UNSUPPORTED_FILE_TYPE') return '지원하지 않는 파일 형식이에요. 엑셀(.xlsx, .xls)·PDF·이미지(.jpg, .png) 파일을 올려 주세요'
  if (status === 422 || code === 'PARSE_FAILED') return '파일을 읽지 못했어요. 파일이 손상되었거나 형식이 올바르지 않을 수 있어요'
  if (e.code === 'ECONNABORTED' || e.code === 'ETIMEDOUT' || status === 408 || status === 504) {
    return '분석 시간이 너무 오래 걸려 중단됐어요. 잠시 후 다시 시도하거나 더 작은 파일을 올려 보세요'
  }
  if (!e.response) return '네트워크 연결이 불안정해요. 연결을 확인한 뒤 다시 시도해 주세요'
  return '서버에 문제가 생겨 분석하지 못했어요. 잠시 후 다시 시도해 주세요'
}

/** 화면 단계 — idle(파일 선택) → analyzing(분석 중) → [fallback(키워드 결과 확인)] */
type Phase = 'idle' | 'analyzing' | 'fallback'

export function UploadView2({ navigate, onResult, onRawResult, onReset, selectedSiteId, isResetFlow }: Props) {
  const user = useAuthStore(s => s.user)

  const [file,         setFile]         = useState<File | null>(null)
  const [phase,        setPhase]        = useState<Phase>('idle')
  // 분석 중 화면에 보여 주는 실제 값만 둔다 — 경과 시간·완료 여부. 파일 올리기는 따로 보여 주지 않는다(분석 중 하나로 묶는다).
  // 서버는 AI 응답이 올 때까지 중간 진행을 알려 주지 않으므로 진행률은 만들지 않는다.
  const [elapsed,      setElapsed]      = useState(0)
  const [finished,     setFinished]     = useState(false)
  const [error,        setError]        = useState('')
  const [retry,        setRetry]        = useState<(() => void) | null>(null)
  const [driveLoading, setDriveLoading] = useState(false)
  const [fallbackRaw,  setFallbackRaw]  = useState<RawExtract | null>(null)
  const drivePicker = useGoogleDrivePicker()

  // 분석 중 이탈·취소 대비 — 진행 중 요청 취소, 경과 시간 타이머·완료 대기 타이머 정리, 언마운트 뒤 결과 무시
  const mountedRef = useRef(true)
  const abortRef   = useRef<AbortController | null>(null)
  const reqRef     = useRef(0)
  const tickRef    = useRef<ReturnType<typeof setInterval> | null>(null)
  const doneRef    = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearTimers = () => {
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null }
    if (doneRef.current) { clearTimeout(doneRef.current); doneRef.current = null }
  }
  const cancelInflight = () => {
    reqRef.current += 1
    abortRef.current?.abort()
    abortRef.current = null
    clearTimers()
  }

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      cancelInflight()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fail = (message: string, again: (() => void) | null) => {
    clearTimers()
    setFinished(false)
    setPhase('idle')
    setError(message)
    setRetry(() => again)
  }

  const commitAndGo = (raw: RawExtract, source: File | null) => {
    onRawResult(raw, source)
    // 다음 단계(공종)에서 쓰도록 표준 공종을 추출결과로 투영
    const works: ExtractWork[] = (raw.standardWorks ?? [])
      .filter(w => w.matchedCode)
      .map(w => ({ workTypeCode: w.matchedCode as string, workTypeName: w.matchedName ?? '', duration: w.duration ?? 1 }))
    onResult({ works, skipped: works.length === 0 })
    navigate('create-extract-raw2')
  }

  const analyze = async (f: File) => {
    if (!user?.compId) { fail('로그인 정보를 확인하지 못했어요. 다시 로그인해 주세요', null); return }
    cancelInflight()
    const reqId = reqRef.current
    const ctrl = new AbortController()
    abortRef.current = ctrl
    setError('')
    setRetry(null)
    setPhase('analyzing')
    setElapsed(0)
    setFinished(false)
    // 경과 시간 — 서버는 AI 응답이 올 때까지 진행을 알려 주지 않아, 남은 시간 대신 지금까지 기다린 시간만 보여 준다
    const startedAt = Date.now()
    tickRef.current = setInterval(() => setElapsed(Math.floor((Date.now() - startedAt) / 1000)), 1000)

    const formData = new FormData()
    formData.append('file', f)
    if (selectedSiteId != null) formData.append('siteId', String(selectedSiteId))
    const headers = { 'X-Comp-Id': String(user.compId) }

    // 외부 LLM 구조화 추출 raw 엔드포인트 — siteId 는 선택 파라미터(서버 분석에는 사용되지 않음)
    // LLM 추출은 수십 초까지 걸릴 수 있어 이 호출만 전역 타임아웃(10s)을 크게 덮어쓴다.
    try {
      const res = await apiClient.post('/schedule/estimate-analyses/raw', formData, {
        headers, timeout: 600000, signal: ctrl.signal,
      })
      if (!mountedRef.current || reqId !== reqRef.current) return
      clearTimers()
      const raw = (res.data?.data ?? null) as RawExtract | null
      const status = raw?.analysisStatus ?? (raw ? 'SUCCESS' : 'FAILED')
      if (!raw || status === 'FAILED') {
        const reason = raw?.failureReason
        const message = reason === 'LLM_PARSE_FAILED'
          ? 'AI가 견적서 내용을 읽어내지 못했어요. 다른 파일이나 시트로 다시 시도해 보세요'
          : reason === 'LLM_UNAVAILABLE'
            ? 'AI 분석 서비스에 연결하지 못했어요. 잠시 후 다시 시도해 주세요'
            : '견적서를 분석하지 못했어요. 잠시 후 다시 시도해 주세요'
        fail(message, () => { void analyze(f) })
        return
      }
      if (status === 'NO_WORKS') {
        // 표준 공종은 0건이어도 비표준 항목이 있으면 오류로 멈추지 않고 추출 결과 확인 화면으로 보낸다
        if ((raw.nonStandardWorks ?? []).length > 0) {
          setFinished(true)
          doneRef.current = setTimeout(() => {
            doneRef.current = null
            if (mountedRef.current && reqId === reqRef.current) commitAndGo(raw, f)
          }, 400)
          return
        }
        fail('견적서에서 공종을 찾지 못했어요. 다른 파일이나 시트를 선택해 보세요', null)
        return
      }
      if (status === 'FALLBACK_KEYWORD') {
        // 안내를 읽은 뒤 [다음]으로 진행한다
        setFallbackRaw(raw)
        setPhase('fallback')
        return
      }
      setFinished(true)
      doneRef.current = setTimeout(() => {
        doneRef.current = null
        if (mountedRef.current && reqId === reqRef.current) commitAndGo(raw, f)
      }, 400)
    } catch (err) {
      if (!mountedRef.current || reqId !== reqRef.current) return
      const message = uploadErrorMessage(err)
      if (message == null) return
      fail(message, () => { void analyze(f) })
    }
  }

  const handleFilesSelected = (files: File[]) => {
    const f = files[0]
    if (!f) return
    // 서버로 보내기 전에 형식·크기를 먼저 막는다
    if (!isValidFile(f)) {
      setError('지원하지 않는 파일 형식이에요. 엑셀(.xlsx, .xls)·PDF·이미지(.jpg, .png) 파일을 올려 주세요')
      setRetry(null)
      return
    }
    if (f.size > MAX_FILE_BYTES) {
      setError(`파일이 너무 커요(${MAX_FILE_MB}MB까지 올릴 수 있어요)`)
      setRetry(null)
      return
    }
    // 새 파일을 던지는 순간 이전 추출·매칭·선택을 전부 비운다 (이전 고객·현장 잔존 방지)
    onReset()
    setFile(f)
    setFallbackRaw(null)
    void analyze(f)
  }

  // 구글 드라이브에서 고른 파일도 로컬 드래그&드롭과 완전히 같은 경로(handleFilesSelected)를 태운다 —
  // 서버(estimate-analyses)는 파일이 어디서 왔는지 구분하지 않는다.
  const handleDriveImport = async () => {
    setError('')
    setRetry(null)
    setDriveLoading(true)
    try {
      const f = await drivePicker.open({ includeDocuments: true })
      if (mountedRef.current && f) handleFilesSelected([f])
    } catch (e) {
      if (mountedRef.current) setError(e instanceof Error ? e.message : '구글 드라이브 연동 중 오류가 발생했어요.')
    } finally {
      if (mountedRef.current) setDriveLoading(false)
    }
  }

  const handleBack = () => {
    navigate(isResetFlow ? 'create-upload' : 'create-site')
  }

  // 분석·시트 확인 중 취소, 시트 선택·키워드 결과 화면에서 파일 다시 고르기
  const resetToIdle = () => {
    cancelInflight()
    setPhase('idle')
    setFinished(false)
    setError('')
    setRetry(null)
    setFile(null)
    setFallbackRaw(null)
    onReset()
  }

  const fileHeader = (right: ReactNode) => (
    <div className="mb-4 flex items-center gap-3 border-b border-border pb-3.5">
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xxl bg-success/15">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--color-success)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
          <polyline points="14 2 14 8 20 8"/>
          <polyline points="9 15 11 17 15 13"/>
        </svg>
      </div>
      <div>
        <div className="text-label text-text">{file?.name}</div>
        <div className="mt-0.5 text-caption text-text-muted">{file ? formatBytes(file.size) : ''}</div>
      </div>
      <div className="ml-auto flex items-center gap-1.5">{right}</div>
    </div>
  )

  return (
    <div className="flex min-h-full items-center justify-center bg-surface px-4 py-6">
      <div className="w-full max-w-[680px]">
        <StepIndicator step={isResetFlow ? 0 : 1} resetMode={!!isResetFlow} quote />

        {phase === 'analyzing' ? (
          /* ── 분석 중 ── */
          <div className="mb-4 rounded-xl border border-border bg-surface-raised p-6">
            {fileHeader(<Badge status="info">{finished ? '분석 완료' : '분석 중'}</Badge>)}

            {/* 파일 올리기와 AI 분석을 따로 나누지 않고 "분석 중" 하나로 보여 준다 — 서버가 진행을 알려 주지 않아 경과 시간만 */}
            <div className="flex items-center justify-between gap-2 text-body-sm">
              <span className="text-label text-text">{finished ? '견적서 분석을 마쳤어요' : '견적서를 AI가 분석 중입니다'}</span>
              {!finished && <span className="text-caption tabular-nums text-text-muted">경과 {elapsed}초</span>}
            </div>
            {!finished && (
              <>
                <Skeleton className="mt-2 h-03" role="status" aria-busy="true" aria-label="AI가 견적서를 분석하는 중" />
                <p className="mt-2 text-caption text-text-muted">견적서 크기와 내용에 따라 수십 초 걸릴 수 있어요. 이 화면을 닫지 말고 기다려 주세요</p>
              </>
            )}
            <div className="mt-4 flex border-t border-border pt-4">
              <Button variant="secondary" size="md" onClick={resetToIdle}>분석 취소</Button>
            </div>
          </div>
        ) : phase === 'fallback' && fallbackRaw ? (
          /* ── AI 분석 실패 → 키워드 기준 결과 확인 ── */
          <div className="mb-4 rounded-xl border border-border bg-surface-raised p-6">
            {fileHeader(null)}
            <Alert kind="warning" role="alert">AI 분석에 실패해 키워드로 찾았어요. 결과를 꼭 확인해 주세요</Alert>
            <div className="mt-4 flex gap-2.5 border-t border-border pt-4">
              <Button variant="secondary" size="lg" onClick={resetToIdle}>다른 파일 선택</Button>
              <Button variant="primary" size="lg" className="flex-1" onClick={() => commitAndGo(fallbackRaw, file)}>
                결과 확인하기
              </Button>
            </div>
          </div>
        ) : (
          <>
            <InfoBanner>
              견적서 파일만 올리면 AI가 공종과 일정을 자동으로 만들어줘요 — 가장 빠르고 쉬운 방법이에요
            </InfoBanner>

            {error && (
              <div className="mb-3.5">
                <ErrorBanner message={error} onRetry={retry ?? undefined} />
              </div>
            )}

            <div className="mb-4">
              {/* 견적서 업로드 — 이 흐름은 견적서만 올리면 자동 추출되는 것이 핵심이라 업로드 단일 카드 */}
              <div className="relative transition-shadow">
                <Upload
                  accept={ACCEPT}
                  multiple={false}
                  onChange={handleFilesSelected}
                  renderEmpty={() => (
                    <>
                      {/* 칩 80 · 아이콘 48(D-09 §15.17). strokeWidth 는 viewBox 24 기준이라 1.5 = 3px 렌더 — 공용 아이콘과 같은 획 */}
                      <div className="dropzone-chip">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
                        </svg>
                      </div>
                      <div className="dropzone-text">
                        <div className="dropzone-title">견적서 파일 업로드</div>
                        <div className="dropzone-desc">
                          <strong className="font-bold text-text">엑셀·PDF·이미지 견적서를 끌어다 놓거나 클릭해 주세요</strong><br />
                          파일을 선택하면 즉시 AI 분석을 시작해요
                        </div>
                        <div className="dropzone-hint">.xlsx · .xls · .pdf · .jpg · .png (50MB까지)</div>
                      </div>
                    </>
                  )}
                />
              </div>
            </div>

            {/* 구글 드라이브에서 가져오기 — 위 카드(.dropzone-trigger, 세로 중앙정렬 고정)와
                달리 가로 행 레이아웃이 필요해 클래스를 재사용하지 않고 같은 톤(점선 테두리·
                --radius-md·--shell-bg)을 직접 지정한다. 설정(env 키) 전이면 눌러도 안내만 뜬다. */}
            <div className="mb-4">
              <div
                role="button"
                tabIndex={0}
                aria-disabled={driveLoading}
                onClick={() => { if (!driveLoading) void handleDriveImport() }}
                onKeyDown={e => { if ((e.key === 'Enter' || e.key === ' ') && !driveLoading) { e.preventDefault(); void handleDriveImport() } }}
                className="flex w-full items-center gap-3.5 text-left transition-colors"
                style={{
                  border: '1px dashed var(--shell-border)', borderRadius: 'var(--radius-md)',
                  background: 'var(--shell-bg)', padding: '14px 20px',
                  opacity: driveLoading ? 0.7 : 1, cursor: driveLoading ? 'default' : 'pointer',
                }}
                onMouseEnter={e => { if (!driveLoading) e.currentTarget.style.borderColor = 'var(--gt-primary)' }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--shell-border)' }}
              >
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xxl bg-group-subtle">
                  {driveLoading ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" className="animate-spin text-group">
                      <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-group">
                      <path d="M20 16.2A4.5 4.5 0 0 0 17.5 8h-1.26A8 8 0 1 0 4 16.25"/>
                      <polyline points="12 12 12 21"/>
                      <polyline points="8.5 17.5 12 21 15.5 17.5"/>
                    </svg>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[13.5px] font-bold text-text">구글 드라이브에서 가져오기</div>
                  <div className="text-[length:var(--type-caption-size)] text-text-muted">드라이브에 저장된 견적서 파일(엑셀·PDF·이미지)을 바로 불러와요</div>
                </div>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 text-text-disabled">
                  <polyline points="9 18 15 12 9 6"/>
                </svg>
              </div>
            </div>

            {/* 파일을 올리고 분석이 끝나야 다음 단계로 넘어간다 — 이 화면에는 [다음] 버튼이 없다 */}
            <div className="mt-4 flex gap-2.5 border-t border-border pt-4">
              <Button variant="secondary" size="lg" onClick={handleBack}>
                이전
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
